import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  IconButton,
  Paginator,
  Spinner,
  Tag,
  TextField,
  TreeTable,
  type TreeNode,
  type TreeTableColumn,
} from '@humanforce/mango-ui-react';
import type {
  PayRunExceptionItem,
  PayRunExceptionsPage,
  PayRunPopulationEmployee,
  PayRunPopulationPage,
  PopulationLoadStatus,
} from './payRunPopulationApiTypes';
import { actionPayRunComplianceFinding, getPayRunExceptions, getPopulation } from './payRunPopulationHttpClient';
import { PayRunBankingPanel } from './PayRunBankingPanel';
import type { BsbValidationDetails } from './bsbValidationApiTypes';
import './PayRunExceptionsTab.css';

interface PayRunExceptionsTabProps {
  payId: string;
  businessRecordId: string;
  populationStatus: PopulationLoadStatus | null | undefined;
  reloadKey?: number;
  onFindingActioned?: () => Promise<void> | void;
  /** Banking (BSB) evidence owned by the Pay Run detail screen and shared with the timeline. */
  banking: BsbValidationDetails | null;
  bankingLoading: boolean;
  bankingError: string | null;
  bankingNotEvaluated: boolean;
  onReloadBanking: () => Promise<void> | void;
  onNotice?: (notice: { tone: 'positive' | 'critical'; title: string; message: string }) => void;
}

interface ComplianceEmployeeRow extends PayRunPopulationEmployee {
  kind: 'employee';
  issues: PayRunExceptionItem[];
  issueTone: 'critical' | 'caution';
  synthetic?: boolean;
}

interface ComplianceIssueRow extends PayRunExceptionItem {
  kind: 'issue';
}

const pageSize = 20;
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });
const words = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const isCritical = (severity: string): boolean => ['blocking', 'cannotstartpay', 'critical', 'failed']
  .some((value) => severity.toLowerCase().replace(/[_\s-]/g, '').includes(value));

const belongsToEmployee = (issue: PayRunExceptionItem, employee: PayRunPopulationEmployee): boolean => {
  const references = [issue.personId, issue.workEntityId, issue.contextId].filter(Boolean);
  return references.includes(employee.personId) || references.includes(employee.employmentRecordId);
};

export const PayRunExceptionsTab = ({
  payId, businessRecordId, populationStatus, reloadKey, onFindingActioned,
  banking, bankingLoading, bankingError, bankingNotEvaluated, onReloadBanking, onNotice,
}: PayRunExceptionsTabProps) => {
  const [page, setPage] = useState(1);
  const [exceptions, setExceptions] = useState<PayRunExceptionsPage | null>(null);
  const [population, setPopulation] = useState<PayRunPopulationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [nextExceptions, nextPopulation] = await Promise.all([
        getPayRunExceptions(payId, { page, pageSize }, signal),
        getPopulation(payId, { page, pageSize, search: '' }, signal),
      ]);
      setExceptions(nextExceptions);
      setPopulation(nextPopulation);
    } catch (loadError) {
      if (!signal?.aborted) setError(loadError instanceof Error ? loadError.message : 'Compliance details could not be loaded.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, payId]);

  const actionFinding = useCallback(async (findingId: string) => {
    setActioningId(findingId);
    setError(null);
    try {
      await actionPayRunComplianceFinding(payId, findingId);
      await load();
      await onFindingActioned?.();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'The finding could not be actioned.');
    } finally {
      setActioningId(null);
    }
  }, [load, onFindingActioned, payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus, reloadKey]);

  const rows = useMemo<ComplianceEmployeeRow[]>(() => {
    if (!exceptions || !population) return [];
    const currentIssues = exceptions.items.filter((issue) => issue.lifecycle !== 'RESOLVED');
    const employeeRows = population.items.flatMap((employee) => {
      const issues = currentIssues.filter((issue) => belongsToEmployee(issue, employee));
      if (issues.length === 0) return [];
      return [{
        ...employee,
        kind: 'employee' as const,
        issues,
        issueTone: issues.some((issue) => isCritical(issue.severity)) ? 'critical' as const : 'caution' as const,
      }];
    });
    const matchedIssueIds = new Set(employeeRows.flatMap((employee) => employee.issues.map((issue) => issue.id)));
    const unmatchedGroups = new Map<string, PayRunExceptionItem[]>();
    currentIssues.filter((issue) => !matchedIssueIds.has(issue.id)).forEach((issue) => {
      const owner = issue.personId ?? issue.workEntityId ?? issue.contextId ?? issue.id;
      unmatchedGroups.set(owner, [...(unmatchedGroups.get(owner) ?? []), issue]);
    });
    const unmatchedRows = [...unmatchedGroups.entries()].map(([owner, issues]): ComplianceEmployeeRow => ({
      kind: 'employee',
      personId: owner,
      employmentRecordId: `compliance-${owner}`,
      employeeNumber: owner,
      displayName: issues[0].displayName ?? 'Pay Run issue',
      roleTitle: 'Compliance review',
      employmentStartDate: '',
      employmentEndDate: null,
      payrollStatus: 'review_required',
      exceptionSeverity: issues.some((issue) => isCritical(issue.severity)) ? 'blocking' : 'warning',
      units: Number.NaN,
      hourlyUnits: Number.NaN,
      gross: Number.NaN,
      taxable: Number.NaN,
      tax: Number.NaN,
      stsl: Number.NaN,
      super: Number.NaN,
      netPay: Number.NaN,
      transactions: [],
      issues,
      issueTone: issues.some((issue) => isCritical(issue.severity)) ? 'critical' : 'caution',
      synthetic: true,
    }));
    return [...employeeRows, ...unmatchedRows];
  }, [exceptions, population]);

  const visibleRows = useMemo(() => {
    if (!search.trim()) return rows;
    const query = search.trim().toLowerCase();
    return rows.filter((employee) => [
      employee.displayName,
      employee.employeeNumber,
      employee.roleTitle,
      ...employee.issues.flatMap((issue) => [issue.code, issue.displayName, issue.message]),
    ].some((value) => String(value ?? '').toLowerCase().includes(query)));
  }, [rows, search]);

  const columns = useMemo<TreeTableColumn[]>(() => {
    const data = (node: TreeNode) => node.data as unknown as ComplianceEmployeeRow | ComplianceIssueRow;
    const bodyClassName = (value: unknown) => {
      const row = value as ComplianceEmployeeRow | ComplianceIssueRow;
      const employeeKey = row.kind === 'employee' ? `employee-${row.personId}-${row.employmentRecordId}` : '';
      const isExpandedEmployee = row.kind === 'employee' && expandedKeys[employeeKey];
      return `pay-run-compliance-table__cell${isExpandedEmployee ? ' pay-run-compliance-table__cell--expanded-parent' : ''}`;
    };
    const presentation = {
      bodyClassName,
      headerClassName: 'pay-run-compliance-table__header',
    };
    const money = (value: number) => Number.isFinite(value) ? currency.format(value) : '—';
    const quantity = (value: number) => Number.isFinite(value) ? number.format(value) : '—';
    return [
      {
        field: 'displayName', header: 'Employee', width: '15.125rem', ...presentation,
        body: (node) => {
          const row = data(node);
          if (row.kind === 'issue') {
            const critical = isCritical(row.severity);
            const action = row.origin === 'compliance' && row.lifecycle === 'OPEN'
              ? <Button label="Mark actioned" variant="secondary" size="small" disabled={actioningId === row.id} onClick={() => void actionFinding(row.id)} />
              : undefined;
            const issueName = row.displayName ?? words(row.code);
            return (
              <div className="pay-run-compliance-issue-block" data-testid={`compliance-issue-${row.id}`}>
                <div
                  className={`pay-run-compliance-issue-card mgo-background-${critical ? 'critical' : 'caution'} mgo-border-${critical ? 'critical' : 'caution'}-soft`}
                  data-testid={`compliance-issue-card-${row.id}`}
                  role="note"
                >
                  <span className={`pay-run-compliance-issue-icon mgo-text-xs mgo-color-${critical ? 'critical' : 'caution'}`}>
                    <Icon name={critical ? 'status-critical' : 'status-caution'} />
                  </span>
                  <span className="pay-run-compliance-issue-copy mgo-text-xs">
                    <strong>{critical ? 'Cannot start pay — ' : 'Warning. '}<span>{issueName}</span>.</strong>
                    <span>{row.message}</span>
                  </span>
                  {action}
                </div>
              </div>
            );
          }
          return <span className="pay-run-compliance-employee"><strong>{row.displayName}</strong><span className="mgo-color-muted">{row.employeeNumber}</span></span>;
        },
      },
      {
        field: 'roleTitle', header: 'Role', width: '8.8125rem', ...presentation,
        body: (node) => data(node).kind === 'employee' ? (data(node) as ComplianceEmployeeRow).roleTitle || '—' : '',
      },
      { field: 'units', header: 'Units', width: '4.125rem', ...presentation, body: (node) => data(node).kind === 'employee' ? quantity(Number((data(node) as ComplianceEmployeeRow).units)) : '' },
      { field: 'hourlyUnits', header: 'Hourly units', width: '6.125rem', ...presentation, body: (node) => data(node).kind === 'employee' ? quantity(Number((data(node) as ComplianceEmployeeRow).hourlyUnits)) : '' },
      { field: 'gross', header: 'Gross', width: '4.875rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).gross)) : '' },
      { field: 'taxable', header: 'Taxable', width: '4.3125rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).taxable)) : '' },
      { field: 'tax', header: 'Tax', width: '4.0625rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).tax)) : '' },
      { field: 'stsl', header: 'STSL', width: '3.9375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).stsl)) : '' },
      { field: 'btd', header: 'BTD', width: '3.9375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? '—' : '' },
      { field: 'super', header: 'Super', width: '3.9375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).super)) : '' },
      { field: 'atd', header: 'ATD', width: '4.625rem', ...presentation, body: (node) => data(node).kind === 'employee' ? '—' : '' },
      { field: 'netPay', header: 'Net pay', width: '5.375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).netPay)) : '' },
      {
        field: 'status', header: 'Status', width: '9.6875rem', ...presentation,
        body: (node) => {
          const row = data(node);
          if (row.kind === 'employee') return <Tag label="Review required" tone={row.issueTone} />;
          return '';
        },
      },
      {
        field: 'actions', header: '', width: '3.6875rem', ...presentation,
        body: (node) => {
          const row = data(node);
          if (row.kind === 'employee' && !row.synthetic) {
            return (
              <IconButton
                icon="more"
                label={`Actions for ${row.displayName}`}
                variant="tertiary"
                size="small"
                href={`/workers/worker-record/personal?worker=${encodeURIComponent(row.personId)}`}
              />
            );
          }
          return '';
        },
      },
    ];
  }, [actionFinding, actioningId, expandedKeys]);

  const treeNodes = useMemo<TreeNode[]>(() => visibleRows.map((employee) => ({
    key: `employee-${employee.personId}-${employee.employmentRecordId}`,
    data: employee,
    children: employee.issues.map((issue) => ({
      key: `issue-${issue.id}`,
      leaf: true,
      data: { ...issue, kind: 'issue' },
    })),
  })), [visibleRows]);

  const totalPages = population?.pagination.totalPages ?? 0;

  return (
    <section className="mgo-stack mgo-gap-l" aria-label="Pay Run compliance" aria-busy={loading}>
      <PayRunBankingPanel
        businessRecordId={businessRecordId}
        payId={payId}
        details={banking}
        loading={bankingLoading}
        error={bankingError}
        notEvaluated={bankingNotEvaluated}
        onReload={onReloadBanking}
        onOverrideApplied={onFindingActioned}
        onNotice={onNotice}
      />

      <div className="mgo-split mgo-align-center mgo-gap-s pay-run-compliance-toolbar">
        <div className="pay-run-compliance-toolbar__search">
          <TextField
            label="Search employees or compliance warnings"
            hideLabel
            placeholder="Search employees or compliance warnings..."
            leadingIcon="search"
            clearable
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            onClear={() => { setSearch(''); setPage(1); }}
          />
        </div>
        {exceptions && <span className="mgo-text-xs mgo-color-muted">{exceptions.summary.affectedPeople} employees</span>}
      </div>

      {exceptions && exceptions.evaluationStatus !== 'completed' && (
        <Callout tone="caution" title="Compliance readiness not confirmed">
          {exceptions.readinessReason ? `Readiness is blocked: ${words(exceptions.readinessReason)}.` : 'Run the readiness evaluation after completing the required inputs and calculation.'}
        </Callout>
      )}
      {populationStatus === 'loading' && <Callout tone="info" title="Refreshing compliance">Results update when the employee snapshot is ready.</Callout>}
      {error && <Callout tone="critical" title="Compliance unavailable" action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}>{error}</Callout>}

      {loading && !error ? (
        <div className="mgo-cluster mgo-justify-center mgo-gap-s" style={{ minHeight: '12rem' }}><Spinner /><span>Loading compliance…</span></div>
      ) : !error && rows.length === 0 ? (
        <EmptyState variant={exceptions?.evaluationStatus === 'completed' ? 'success' : 'no-data'} title="No current compliance issues" description={exceptions?.evaluationStatus === 'completed' ? 'The latest canonical evaluation has no findings or outstanding input review issues.' : 'No findings are persisted, but readiness has not been confirmed.'} />
      ) : !error && treeNodes.length === 0 ? (
        <EmptyState variant="no-data" title="No employees match your search" description="Change or clear the current search." actions={[<Button key="clear" label="Clear search" variant="secondary" onClick={() => setSearch('')} />]} />
      ) : !error ? (
        <div className="pay-run-compliance-table">
          <TreeTable
            value={treeNodes}
            columns={columns}
            expandedKeys={expandedKeys}
            onToggle={setExpandedKeys}
            scrollable
            testId="pay-run-exceptions-table"
            ptProps={{
              rowClassName: (node) => ({
                'pay-run-compliance-employee-row--critical': node.data.kind === 'employee' && node.data.issueTone === 'critical',
                'pay-run-compliance-employee-row--caution': node.data.kind === 'employee' && node.data.issueTone === 'caution',
                'pay-run-compliance-issue-row--critical': node.data.kind === 'issue' && isCritical(node.data.severity),
                'pay-run-compliance-issue-row--caution': node.data.kind === 'issue' && !isCritical(node.data.severity),
              }),
              tableStyle: { minWidth: '82.625rem', width: '100%' },
            }}
            emptyMessage="No compliance issues."
          />
        </div>
      ) : null}

      {!loading && !error && population && totalPages > 1 && <Paginator first={(page - 1) * pageSize}
        itemsPerPage={pageSize} totalRecords={population.pagination.totalItems}
        onPageChange={(event) => setPage(event.page + 1)} />}
    </section>
  );
};
